// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as api from '../../api/client'
import { EmailSuggestField, type EmailSuggestion } from '../../components/ui/FormFields'

export interface EmailChoice { email: string; saveToProfile: boolean }

/** A single mailbox — refuses what the server's IsAddress refuses (spaces, a second @, `,` / `;` lists). */
export const isEmail = (s: string) => /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(s.trim())

/** The PioneerRx primary first, then saved alternates (mig 420), each address once. */
export function emailSuggestions(primary: string | null, alts: api.ContactEmail[]): EmailSuggestion[] {
  const out: EmailSuggestion[] = []
  const seen = new Set<string>()
  const add = (email: string, hint: string) => {
    const k = email.trim().toLowerCase()
    if (!k || seen.has(k)) return
    seen.add(k); out.push({ email, hint })
  }
  if (primary) add(primary, 'PioneerRx')
  for (const a of alts) add(a.email, a.label || 'other')
  return out
}

/**
 * Recipient email for the e-sign send dialogs. The operator types any address or picks one of the
 * PioneerRx primary + saved alternates (mig 420). A typed address that is new and valid can be saved
 * to the subject's Other emails (patient or prescriber) for next time.
 *
 * ASP 2026-10-14 #6: this was a pick-only typeahead, so a typed address was only a search term. On Tab
 * the box went back to the primary and the keystrokes landed in the next field (the interpreter email).
 * The box is now the value itself, and the contact list loading late never replaces what was typed.
 */
export default function EmailPicker({ subjectType, subjectId, roleLabel, fallbackEmail, onChange }: {
  subjectType: 'patient' | 'prescriber'
  subjectId?: string | null
  roleLabel: string                 // 'patient' | 'provider' — used in the save label
  fallbackEmail?: string            // the on-file email known to the caller (used before/without a fetch)
  onChange: (c: EmailChoice) => void
}) {
  const [email, setEmail] = useState(fallbackEmail || '')
  const [save, setSave] = useState(false)
  const touched = useRef(false)
  const q = useQuery({
    queryKey: ['contact-emails', subjectType, subjectId],
    queryFn: () => api.listContactEmails(subjectType, subjectId!),
    enabled: !!subjectId,
    retry: false,          // without the list the operator can still type; the fallback stands
  })
  const primary = q.data?.primary_email ?? fallbackEmail ?? null
  const alts = q.data?.alternates ?? []

  // Seed the box from the loaded list only until the operator has typed or picked.
  useEffect(() => {
    if (!q.data || touched.current) return
    setEmail(q.data.primary_email || fallbackEmail || q.data.alternates[0]?.email || '')
  }, [q.data])                       // eslint-disable-line react-hooks/exhaustive-deps

  const suggestions = useMemo(() => emailSuggestions(primary, alts), [primary, q.data])  // eslint-disable-line react-hooks/exhaustive-deps
  const trimmed = email.trim()
  const valid = isEmail(trimmed)
  const isNew = valid && !suggestions.some(s => s.email.trim().toLowerCase() === trimmed.toLowerCase())

  useEffect(() => {
    onChange({ email: trimmed, saveToProfile: isNew && save && !!subjectId })
  }, [trimmed, isNew, save, subjectId])    // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ marginTop: 4 }}>
      <EmailSuggestField value={email} suggestions={suggestions} ariaLabel={`${roleLabel} email`}
        placeholder="name@example.com"
        onChange={v => { touched.current = true; setEmail(v) }}
        error={!!trimmed && !valid} helperText={trimmed && !valid ? 'Enter a full email address, e.g. name@example.com' : undefined} />
      {isNew && (subjectId ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginTop: 6, color: 'var(--color-text-soft)', cursor: 'pointer' }}>
          <input type="checkbox" checked={save} onChange={e => setSave(e.target.checked)} />
          Save to this {roleLabel}&rsquo;s profile (Other emails)
        </label>
      ) : (
        // No subject record to attach the address to, so there is nothing to save it against.
        <div style={{ fontSize: 12, marginTop: 6, color: 'var(--color-text-mute)' }}>
          This send only — no {roleLabel} record on file to save the address to.
        </div>
      ))}
    </div>
  )
}
